import axios from "axios";

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/v1/api/inventory/manual`;
const INVENTORY_READ_TIMEOUT_MS = 12000;
const DAILY_INVENTORY_CACHE_TTL_MS = 2 * 60 * 1000;
const dailyInventoryCache = new Map();
const dailyInventoryRequests = new Map();
const dailyInventoryCacheVersions = new Map();

const getDailyInventoryCacheKey = ({
  cacheScope,
  businessDate,
  mode,
}) => `${cacheScope}:${businessDate}:${mode}`;

const requestConfig = (token, options = {}) => ({
  ...options,
  headers: {
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  },
});

export const fetchDailyInventoryStatus = async ({ token, businessDate }) => {
  const { data } = await axios.get(
    `${baseUrl}/daily-status`,
    requestConfig(token, {
      params: { businessDate },
      timeout: INVENTORY_READ_TIMEOUT_MS,
    })
  );
  return data;
};

export const getCachedDailyInventory = ({
  cacheScope,
  businessDate,
  mode,
}) => {
  if (!cacheScope) return null;
  const key = getDailyInventoryCacheKey({
    cacheScope,
    businessDate,
    mode,
  });
  const cached = dailyInventoryCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.cachedAt > DAILY_INVENTORY_CACHE_TTL_MS) {
    dailyInventoryCache.delete(key);
    return null;
  }
  return cached.data;
};

export const fetchDailyInventory = async ({
  token,
  cacheScope,
  businessDate,
  mode,
  forceRefresh = false,
}) => {
  const key = getDailyInventoryCacheKey({
    cacheScope,
    businessDate,
    mode,
  });
  if (!forceRefresh) {
    const cached = getCachedDailyInventory({
      cacheScope,
      businessDate,
      mode,
    });
    if (cached) return cached;
  }
  if (dailyInventoryRequests.has(key)) {
    return dailyInventoryRequests.get(key);
  }

  const cacheVersion = dailyInventoryCacheVersions.get(key) || 0;
  const request = axios
    .get(
      `${baseUrl}/${mode}`,
      requestConfig(token, {
        params: { businessDate },
        timeout: INVENTORY_READ_TIMEOUT_MS,
      })
    )
    .then(({ data }) => {
      if (
        (dailyInventoryCacheVersions.get(key) || 0) === cacheVersion
      ) {
        dailyInventoryCache.set(key, {
          cachedAt: Date.now(),
          data,
        });
      }
      return data;
    })
    .finally(() => {
      if (dailyInventoryRequests.get(key) === request) {
        dailyInventoryRequests.delete(key);
      }
    });

  dailyInventoryRequests.set(key, request);
  return request;
};

export const invalidateDailyInventoryCache = ({
  cacheScope,
  businessDate,
}) => {
  ["beginning", "ending"].forEach((mode) => {
    const key = getDailyInventoryCacheKey({
      cacheScope,
      businessDate,
      mode,
    });
    dailyInventoryCache.delete(key);
    dailyInventoryRequests.delete(key);
    dailyInventoryCacheVersions.set(
      key,
      (dailyInventoryCacheVersions.get(key) || 0) + 1
    );
  });
};

export const saveDailyInventoryProgress = async ({
  token,
  businessDate,
  mode,
  items,
}) => {
  const { data } = await axios.put(
    `${baseUrl}/${mode}`,
    { businessDate, items },
    requestConfig(token)
  );
  return data;
};

export const completeDailyInventory = async ({
  token,
  businessDate,
  mode,
}) => {
  const { data } = await axios.post(
    `${baseUrl}/${mode}/complete`,
    { businessDate },
    requestConfig(token)
  );
  return data;
};

export const correctDailyInventory = async ({
  token,
  businessDate,
  mode,
  itemId,
  quantity,
  unit,
  reason,
}) => {
  const { data } = await axios.post(
    `${baseUrl}/${mode}/correct`,
    { businessDate, itemId, quantity, unit, reason },
    requestConfig(token)
  );
  return data;
};

export const getDailyInventoryErrorMessage = (
  error,
  fallback = "Unable to update the inventory. Please try again."
) => error?.response?.data?.message || fallback;
